import * as React from 'react';

import { useApolloClient } from '@apollo/client';

import GenerateEmailContentQueryDoc from 'bundles/enterprise-admin-messaging-v2/queries/GenerateEmailContent.graphql';
import type {
  GenerateEmailContentQuery,
  GenerateEmailContentQueryVariables,
} from 'bundles/enterprise-admin-messaging-v2/queries/__generated__/GenerateEmailContent';

/** The message fields the generator fills in. Audience/CTA URL are resolved elsewhere. */
export type GeneratedEmailContent = {
  subject: string;
  body: string;
  callToAction: string;
};

export type GenerateEmailContentResult =
  | { status: 'success'; content: GeneratedEmailContent }
  | { status: 'error'; error: string };

/** Give up on the generate request after this long and surface an error. */
const GENERATE_TIMEOUT_MS = 10000;

/**
 * Calls the `generateEmailContent` gateway query (AI Coach behind the scenes)
 * and normalises its Success/Failure union into a discriminated result.
 *
 * Uses an imperative `client.query` rather than `useLazyQuery`: generation (and
 * the tone rewrites that reuse this hook) fire on a click and just need a
 * one-shot promise. A `useLazyQuery` watched query re-runs on every re-render
 * after the first execute, which caused repeated requests once a rewrite
 * updated the draft and re-rendered the modal. `no-cache` keeps each call fresh
 * (the same prompt should re-generate, and the union has no normalisable id).
 *
 * Only the message copy (subject/body/CTA label) comes from this endpoint;
 * audience resolution and the CTA destination URL are handled separately.
 */
export const useGenerateEmailContent = () => {
  const client = useApolloClient();
  const [loading, setLoading] = React.useState(false);

  const generate = React.useCallback(
    async (prompt: string): Promise<GenerateEmailContentResult> => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      setLoading(true);
      try {
        // Race the request against a hard timeout so a slow/hung Coach call
        // doesn't leave the admin stuck on the loading screen.
        const timeout = new Promise<never>((_resolve, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Email generation timed out.')), GENERATE_TIMEOUT_MS);
        });
        const queryPromise = client.query<GenerateEmailContentQuery, GenerateEmailContentQueryVariables>({
          query: GenerateEmailContentQueryDoc,
          variables: { input: { prompt } },
          fetchPolicy: 'no-cache',
        });
        const { data } = await Promise.race([queryPromise, timeout]);
        const result = data?.EnterpriseEmailContentGeneration?.generateEmailContent;
        if (result?.__typename === 'EnterpriseEmailContentGeneration_Success') {
          const { subject, body, callToAction } = result.content;
          return {
            status: 'success',
            content: { subject: subject ?? '', body: body ?? '', callToAction: callToAction ?? '' },
          };
        }
        if (result?.__typename === 'EnterpriseEmailContentGeneration_Failure') {
          return { status: 'error', error: result.error ?? '' };
        }
        return { status: 'error', error: 'Unexpected empty response from email generation.' };
      } catch (e) {
        return { status: 'error', error: e instanceof Error ? e.message : 'Unknown error' };
      } finally {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        setLoading(false);
      }
    },
    [client]
  );

  return { generate, loading };
};

export default useGenerateEmailContent;
